import { useEffect, useState } from 'react';

/*
  useTransitionState — state machine для popover/drawer/modal lifecycle.

  Решает корневую проблему mount/unmount: компонент должен жить ещё N
  миллисекунд после смены open=false, чтобы exit-анимация отыграла, и
  только потом unmount.

  Phases:
    closed   — не отрисовываем
    opening  — отрисован, играет enter-анимация
    open     — стабильное состояние, интерактивен
    closing  — отрисован, играет exit-анимация

  Использование:
    const { phase, shouldRender, isExiting } = useTransitionState(open, {
      enter: 320,
      exit: 240,
    });
    if (!shouldRender) return null;
    return <div className={isExiting ? 'exit-anim' : 'enter-anim'}>...</div>;
*/

export type TransitionPhase = 'closed' | 'opening' | 'open' | 'closing';

interface TransitionConfig {
  enter: number; // ms
  exit: number; // ms
}

interface TransitionState {
  phase: TransitionPhase;
  shouldRender: boolean;
  isExiting: boolean;
  isEntering: boolean;
}

export function useTransitionState(open: boolean, config: TransitionConfig): TransitionState {
  const [transition, setTransition] = useState<{ open: boolean; phase: TransitionPhase }>({
    open,
    phase: open ? 'open' : 'closed',
  });

  // Adjust the phase before committing children. The effect owns only its completion timer.
  if (transition.open !== open) {
    setTransition({ open, phase: open ? 'opening' : 'closing' });
  }
  const { phase } = transition;

  useEffect(() => {
    if (phase !== (open ? 'opening' : 'closing')) return;
    const timer = window.setTimeout(
      () => setTransition({ open, phase: open ? 'open' : 'closed' }),
      open ? config.enter : config.exit,
    );
    return () => window.clearTimeout(timer);
  }, [open, phase, config.enter, config.exit]);

  return {
    phase,
    shouldRender: phase !== 'closed',
    isExiting: phase === 'closing',
    isEntering: phase === 'opening',
  };
}
